import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import IntegrationOverlay from 'src/pages/IntegrationOverlay';
import { toast } from 'react-toastify';
import logger from 'src/utils/logger';
import { Container } from '../components/layouts/Container/Container';
import styles from './styles/TrackingAuthForm.module.scss';
import { formatConnectorHost, formatDateAndTime, resolveBadgeTypeForStatus } from '../utils/general';
import CardContainer from '../components/layouts/CardContainer/CardContainer';
import Image from '../components/ui/Image/Image';
import Icon from '../components/ui/Icon/Icon';
import Button, { ButtonPositionProps, ButtonToneProps } from '../components/ui/Button/Button';
import { useConnectorErrorsQuery, useConnectorMutations, useConnectorQuery } from '../services/connector';
import LoadingSkeleton from '../components/ui/LoadingSkeleton/LoadingSkeleton';
import CalculatedResultsTable from '../components/tables/CalculatedResultsTable/CalculatedResultsTable';
import ConnectorErrorsTable from '../components/tables/ConnectorErrorsTable/ConnectorErrorsTable';
import TabBar from '../components/layouts/TabBar/TabBar';
import useFilters from '../services/filters';
import Overlay from '../components/ui/Overlay/Overlay';
import OverlayHead from '../components/ui/Overlay/OverlayHead';
import { OverlayRouteProvider } from '../providers/OverlayProvider';
import { useNavigationItems } from '../hooks/useNavigationItems';
import { useProfile } from '../utils/ProfileService';
import { hideSpinnerToast, showSpinnerToast } from '../utils/spinnerToaster';

function IntegrationDetails() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { profile } = useProfile();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const filterInstance = useFilters('res.partner');
  const numericId = Number(id);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [activeResultsTab, setActiveResultsTab] = useState<number>(0);
  if (!id && Number.isNaN(numericId)) {
    throw new Error(`Invalid integration ID: ${id}`);
  }

  const { data } = useConnectorQuery(numericId);
  // size 1: only the total is needed for the tab badge — the tab content
  // paginates with its own query.
  const { data: connectorErrorsMeta } = useConnectorErrorsQuery(numericId, { page: 1, size: 1 });
  const errorCount = connectorErrorsMeta?.total ?? 0;

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('integrations');
  const { testConnectionMutation, runOperationMutation } = useConnectorMutations();
  const { data: connector, isLoading } = useConnectorQuery(numericId);
  const [runningOperations, setRunningOperations] = React.useState<number[]>([]);

  const testConnection = async () => {
    logger.info('Testing connection', numericId);
    showSpinnerToast(t(`integrations.testing_connection`));

    try {
      await testConnectionMutation.mutateAsync({
        params: { path: { connector_id: numericId } },
      });
      toast.success(t(`success.test_connection_success`), { autoClose: 5000 });
    } catch (error: any) {
      logger.error('Connection test failed', error);

      const backendMessage =
        error?.response?.data?.user_msg || error?.response?.data?.detail || error?.message || t('errors.unknown');

      toast.error(t('errors.test_connection_failed', { error: backendMessage }), { autoClose: 10000 });
    } finally {
      hideSpinnerToast();
      setRunningOperations(prev => prev.filter(id => id !== numericId));
    }
  };

  const onRunOperation = async (operationId: number) => {
    logger.info('Running integration operation', operationId);
    setRunningOperations(prev => [...prev, operationId]);
    const operation = data?.operations?.find(op => op.id === operationId);
    const operationType = operation?.name?.includes('export') ? 'export' : 'import';
    showSpinnerToast(t(`integrations.${operationType}_running`));

    try {
      await runOperationMutation.mutateAsync({
        params: { path: { connector_id: numericId, operation_id: operationId } },
      });
      toast.success(t(`success.${operationType}_running_success`), { autoClose: 5000 });
    } catch (error: any) {
      logger.error(`Integration ${operationType} failed`, error);
      toast.error(t(`errors.${operationType}_running_failed`, { autoClose: 10000 }));
    } finally {
      hideSpinnerToast();
      setRunningOperations(prev => prev.filter(id => id !== operationId));
    }
  };

  const displayHost = formatConnectorHost(connector?.host);

  return (
    <Container
      title={`${t('integrations.integrations')} / ${t('integrations.dashboard')} ${
        window.innerWidth > 768 ? `/ ${connector?.name}` : ''
      }`}
      className={classNames(styles.root, 'relative py-32 flex-1')}>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <div className="flex flex-col w-full gap-[32px]">
          <div className="flex flex-col items-start w-full gap-[12px]">
            <div className="flex items-center gap-10 flex-1 min-w-0">
              <h1 className="typo-title-24 md:typo-title-36 max-w-[230px] md:max-w-none truncate overflow-hidden text-ellipsis whitespace-nowrap">
                {connector?.name}
              </h1>
              <Badge
                label="Active"
                expanded
                showDot
                dotFilled
                type={resolveBadgeTypeForStatus(connector?.active ? 'successful' : 'inactive')}
              />
            </div>
            {displayHost && (
              <span className="typo-text-14 text-faded truncate max-w-full" title={connector?.host ?? undefined}>
                {displayHost}
              </span>
            )}
            <p className="typo-text-12 text-faded">{connector?.template?.channel?.description}</p>
          </div>
          <CardContainer
            isRow
            className="flex flex-col gap-[12px] lg:gap-0 lg:flex-row justify-between items-start lg:items-center w-full h-auto lg:min-h-[84px] p-[20px]">
            <div className="flex flex-col lg:flex-row gap-10 items-start lg:items-center w-full">
              <Image
                className="w-[40px] h-[40px]"
                imageUrl={connector?.template?.channel?.logo_url || ''}
                alt="integration"
                fallbackComponent={<Icon name="package" width={20} height={20} />}
              />
              <div className="flex flex-col min-w-0">
                <span className="typo-title-20 text-full max-w-[230px] md:max-w-none truncate overflow-hidden text-ellipsis whitespace-nowrap">
                  {connector?.name}
                </span>
                {displayHost && (
                  <span
                    className="typo-text-12 text-faded truncate max-w-[230px] md:max-w-none"
                    title={connector?.host ?? undefined}>
                    {displayHost}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col lg:flex-row gap-[12px] lg:gap-10 items-start lg:items-center justify-end w-full">
              {accessRights?.read_write && (
                <>
                  <Button
                    title={t('integrations.test_connection')}
                    className="!max-h-none h-[48px] max-w-[145px] w-full"
                    tone={ButtonToneProps.PRIMARY_BLUE}
                    onClick={() => testConnection()}
                  />
                  <Button
                    title={t('general.edit')}
                    className="!max-h-none h-[48px] max-w-[145px] w-full"
                    tone={ButtonToneProps.PRIMARY_BLUE}
                    onClick={() => setShowEdit(true)}
                  />
                </>
              )}
            </div>
          </CardContainer>
          {data?.operations?.map((operation: any, index: number) => {
            const hasInputField = operation?.last_executed;
            return (
              <div key={operation.id} className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="typo-text-16--bold">{operation.title}</span>
                  <Button
                    title={runningOperations.includes(operation.id) ? t('integrations.running') : t('integrations.run')}
                    tone={ButtonToneProps.SECONDARY}
                    iconPosition={ButtonPositionProps.BEFORE}
                    icon="runImport"
                    disabled={runningOperations.includes(operation.id)}
                    onClick={() => onRunOperation(operation.id)}
                  />
                </div>

                {hasInputField && (
                  <div className="mt-2 flex flex-col md:flex-row items-start md:items-center gap-x-8 gap-y-2">
                    <div className="typo-text-14--bold text-faded max-w-[205px] w-full">
                      {t('general.last_execution')}
                    </div>
                    <span className="max-w-[360px] w-full typo-text-14">
                      {operation.last_executed
                        ? formatDateAndTime(
                            operation.last_executed,
                            profile.lang ? profile.lang.replace('_', '-') : 'en-GB',
                          )
                        : ' — '}
                    </span>
                  </div>
                )}

                {data?.operations?.length && index < data.operations.length - 1 && (
                  <div className="mt-2 pb-2 border-b border-color-grey-200" />
                )}
              </div>
            );
          })}
          {/* <StatisticRow items={kpiData} /> */}
          {/* <LineChartCard title={`${connector?.name} Marketplace`} kpi={{ percentage: 12, value: '1.23h' }} /> */}
          <CardContainer isRow className="flex flex-col w-full items-start gap-20">
            <TabBar
              className="w-full"
              tabs={[
                { label: t('integrations.operations_done'), key: 'operations' },
                {
                  label: t('integrations.errors'),
                  key: 'errors',
                  badgeNumber: errorCount,
                  entriesColor: BadgeType.Error,
                },
              ]}
              onActiveTabChange={setActiveResultsTab}
            />
            <div className="flex w-full" ref={containerRef}>
              {activeResultsTab === 0 ? (
                <CalculatedResultsTable containerRef={containerRef} filterInstance={filterInstance} />
              ) : (
                <ConnectorErrorsTable containerRef={containerRef} />
              )}
            </div>
          </CardContainer>
        </div>
      )}
      {showEdit && (
        // onClose leer: Outside-Click/Escape dürfen das Edit-Overlay nicht schließen —
        // das DateTimePicker-Popup rendert in ein Body-Portal und würde sonst als
        // Outside-Click das Formular samt Eingaben verwerfen.
        <OverlayRouteProvider onClose={() => {}}>
          <Overlay>
            <OverlayHead onCloseOverlay={() => setShowEdit(false)} />
            <IntegrationOverlay editMode onClose={() => setShowEdit(false)} />
          </Overlay>
        </OverlayRouteProvider>
      )}
    </Container>
  );
}

export default IntegrationDetails;
